import { useState, useRef, useCallback, useEffect, useContext } from "react";
import { Button, Container, Row, Col, Form } from "react-bootstrap";
import { ArrowUpCircle } from "react-bootstrap-icons";
import UploadTypeIcon, { getTypeMeta, getTypeBadge } from "./UploadTypeIcon";
import FileDropZone from "./FileDropZone";
import FileList from "./FileList";
import AssignModal from "./AssignModal";
import { HelpToolTip } from "./HelpToolTip";
import {
  hexToRgba,
  VALID_EXTENSIONS,
  FILE_SIZE_LIMIT,
  TOTAL_SIZE_LIMIT,
  getTotalSize,
} from "./Utils";
import "./Upload.css";
import { withHeaderMessages } from "../../higherOrderComponents/WithHeaderMessages";
import { WithApiAccess } from "../../lib/HigherOrderComponents/WithApiAccess";
import { Panel } from "../../utils/Panel";
import DocumentUploadHistory from "./DocumentUploadHistory";
import { LoadingSpinner } from "../../lib/components/LoadingSpinner";
import { useParams, Link } from "react-router-dom";
import { DocumentUploadSuccess } from "./DocumentUploadSuccess";
import { canAcceptCreditCard } from "../../utils/providers";
import { AuthContext } from "../../lib/contexts/AuthContext";

const UploadPage = (props) => {
  const [selectedType, setSelectedType] = useState(null);
  const [files, setFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoadingDocumentTypes, setIsLoadingDocumentTypes] = useState(true);
  const [isLoadingContractFileTypes, setIsLoadingContractFileTypes] =
    useState(true);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadTypes, setUploadTypes] = useState([]);
  const [contractFilesUploadTypes, setContractFilesUploadTypes] = useState([]);
  const [defaultContract, setDefaultContract] = useState(null);
  const [fetchedContract, setFetchedContract] = useState(null);
  const addMoreRef = useRef(null);
  const [customerCode, setCustomerCode] = useState("");
  const [user, setUser] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { "*": contractNumber } = useParams();

  const { hasClaim } = useContext(AuthContext);

  useEffect(() => {
    async function getDocumentUploadTypes() {
      props.header.setHeader("documentUpload");
      await props.api
        .get("/api/documents/uploads/types")
        .then((response) => (response.status === 200 ? response.data : null))
        .then((data) => {
          setUploadTypes(data || []);
          setIsLoadingDocumentTypes(false);
        })
        .catch(() => setIsLoadingDocumentTypes(false));
    }
    getDocumentUploadTypes();
  }, []);

  useEffect(() => {
    async function getContractFileTypes() {
      props.header.setHeader("documentUpload");
      await props.api
        .get("/api/documents/uploads/contract-files/types")
        .then((response) => (response.status === 200 ? response.data : null))
        .then((data) => {
          setContractFilesUploadTypes(data || []);
          setIsLoadingContractFileTypes(false);
        })
        .catch(() => setIsLoadingContractFileTypes(false));
    }
    getContractFileTypes();
  }, []);

  useEffect(() => {
    async function getContractDetails() {
      if (!contractNumber) return;
      setIsLoadingContract(true);
      await props.api
        .get(`/api/contract/${contractNumber}`)
        .then((r) => (r.status === 200 ? r.data : null))
        .then((data) => {
          if (data) {
            setDefaultContract(data);
            setFetchedContract(data);
            setCustomerCode(data?.provider?.customerCode);
          }
          setIsLoadingContract(false);
        });
    }
    getContractDetails();
  }, [contractNumber]);

  useEffect(() => {
    async function getCustomerProfile() {
      await props.api
        .get("/api/user")
        .then((response) => {
          if (response.status === 200) {
            return response.data;
          }
        })
        .then((data) => {
          setUser(data);
          if (data.customers.length === 1) {
            setCustomerCode(data.customers[0].code);
          }
        })
        .then(() => setIsLoadingUserDetails(false))
        .catch(() => setIsLoadingUserDetails(false));
    }
    getCustomerProfile();
  }, []);

  const totalSize = getTotalSize(files);
  const isOverTotalLimit = totalSize > TOTAL_SIZE_LIMIT;

  const addFiles = useCallback(
    (fileList) => {
      const newFiles = Array.from(fileList).map((f) => {
        const ext = f.name.split(".").pop().toLowerCase();
        const isValidType = VALID_EXTENSIONS.includes(ext);
        const isValidSize = f.size <= FILE_SIZE_LIMIT;
        const error = !isValidType ? "type" : !isValidSize ? "size" : null;
        return {
          id: Math.random().toString(36).slice(2),
          name: `${f.name}`,
          size: f.size,
          file: f,
          contract: defaultContract ?? null,
          error,
        };
      });
      setFiles((prev) => prev.concat(newFiles));
    },
    [defaultContract],
  );

  function removeFile(id) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function handleTypeSelect(type) {
    setSelectedType(type);
    setFiles([]);
  }

  const documentUploadRequestIsValid = () => {
    const isValidFileSize = files.every((f) => f.size <= FILE_SIZE_LIMIT);
    const isValidTotalSize = !isOverTotalLimit;
    return (
      isValidFileSize &&
      isValidTotalSize &&
      selectedType !== null &&
      !!customerCode
    );
  };

  const submitUpload = async (assignedFiles) => {
    if (isOverTotalLimit) return;
    setIsLoading(true);

    const formData = new FormData();
    const metadata = [];

    const sourceFiles = assignedFiles ?? files;

    sourceFiles.forEach((f) => {
      formData.append("documents", f.file, f.file.name);

      metadata.push({
        fileName: f.file.name,
        contractFileType: f.docType ?? null,
        contractNumber: f.contract?.number,
        providerCode: f.contract?.provider?.code ?? null,
        displayName: f.name ?? f.file.name,
      });
    });

    formData.append("metadata", JSON.stringify(metadata));

    formData.append("documentUploadTypeId", selectedType);
    formData.append("customerCode", customerCode);
    if (documentUploadRequestIsValid()) {
      await props.api
        .postForm("/api/documents/uploads", formData)
        .then((response) => {
          if (response.status === 200) setUploadSuccess(true);
        })
        .catch(() => setIsLoading(false))
        .then(() => setIsLoading(false));
    }
  };

  const handleDocumentUploadSubmitted = async (assignedFiles) => {
    if (customerCode) {
      await submitUpload(assignedFiles);
    }
  };

  const handleCustomerCodeChanged = (event) => {
    setCustomerCode(event.target.value);
    if (fetchedContract?.provider?.customerCode !== event.target.value) {
      setDefaultContract(null);
    } else {
      setDefaultContract(fetchedContract);
    }
  };

  const isContractFiles = selectedType === 6;
  const hasContractFilesClaim = hasClaim("DocumentUploadsContractFiles");
  const fetchingRequests =
    isLoadingContract ||
    isLoadingDocumentTypes ||
    isLoadingContractFileTypes ||
    isLoadingUserDetails;

  const filteredUploadTypes = hasContractFilesClaim
    ? uploadTypes
    : uploadTypes.filter((t) => t.id !== 6);

  if (uploadSuccess) {
    return (
      <DocumentUploadSuccess
        documentUploadTypeId={selectedType}
        user={user}
        documentUploadTypes={filteredUploadTypes}
      />
    );
  }

  const fileAcceptValue = VALID_EXTENSIONS.map((ext) => `.${ext}`).join(",");
  const customerSelectionRequired = user?.customers.length > 1;

  return (
    <>
      <Container style={{ maxWidth: "1500px" }}>
        {fetchingRequests || isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Panel
              header="Upload Documents"
              eventKey="1"
              style={{ marginTop: "10px" }}
            >
              <Col>
                <p className="upload-panel-desc mb-0">
                  PDF, Word, and Excel documents can be uploaded through this
                  secure web page.
                </p>
              </Col>
              <Row>
                <Col xs="auto" className="mt-3 mb-3">
                  {user?.customers && user?.customers.length > 1 && (
                    <Form.Group
                      controlId="customerCode"
                      className="upload-customer-field"
                    >
                      <Form.Label className="upload-customer-label">
                        Select Customer
                      </Form.Label>

                      <Form.Select
                        value={customerCode}
                        name="customerCode"
                        className="upload-customer-select"
                        onChange={handleCustomerCodeChanged}
                      >
                        <option value="">Choose a customer</option>
                        {user.customers.map((c, i) => (
                          <option value={c.code} key={i}>
                            {c.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                </Col>
              </Row>
              <Row className="justify-content-start">
                <Col xs={3}>
                  <div className="upload-section-label">Document type</div>
                  <Row className="g-1 flex-column">
                    {filteredUploadTypes.map((t) => {
                      const isSelected = selectedType === t.id;
                      const meta = getTypeMeta(t.name);
                      const badgeInfo = getTypeBadge(t.name);
                      return (
                        <Col key={t.id}>
                          <button
                            type="button"
                            className={
                              "upload-type-tile" +
                              (isSelected ? " is-selected" : "")
                            }
                            onClick={() => handleTypeSelect(t.id)}
                          >
                            <div
                              className="upload-type-tile__icon-box"
                              style={{
                                background: isSelected
                                  ? hexToRgba(meta.color, 0.8)
                                  : hexToRgba(meta.color, 0.1),
                              }}
                            >
                              <UploadTypeIcon
                                name={t.name}
                                size={16}
                                color={isSelected ? "#fff" : meta.color}
                              />
                            </div>
                            <div className="upload-type-tile__text">
                              <div className="upload-type-tile__label">
                                {t.name}
                              </div>
                              <div className="upload-type-tile__description">
                                {t.description}
                              </div>
                            </div>
                            <div className="upload-type-tile__right">
                              {badgeInfo && !isSelected && (
                                <span
                                  className="upload-type-tile__badge"
                                  style={{ background: badgeInfo.badgeColor }}
                                >
                                  {badgeInfo.badge}
                                </span>
                              )}
                              {isSelected && (
                                <div className="upload-type-tile__check">✓</div>
                              )}
                            </div>
                          </button>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>

                {/* ── Divider ── */}
                <Col xs="auto" className="px-3">
                  <div className="upload-layout__divider h-100" />
                </Col>

                {/* ── Right: file panel ── */}
                <Col className="align-items-center">
                  <Row className="mb-2">
                    <Col>
                      <div className="upload-section-label">
                        {selectedType
                          ? isContractFiles
                            ? "Select documents"
                            : "Upload file"
                          : "Files"}
                      </div>
                    </Col>
                  </Row>
                  {(!selectedType || !customerCode) && (
                    <Row>
                      <Col>
                        <div className="upload-empty-state">
                          <span className="upload-empty-state__arrow">←</span>
                          {customerSelectionRequired ? (
                            <span className="upload-empty-state__text">
                              Select customer and an upload type first
                            </span>
                          ) : (
                            <span className="upload-empty-state__text">
                              Select an upload type first
                            </span>
                          )}
                        </div>
                      </Col>
                    </Row>
                  )}
                  {selectedType && customerCode && (
                    <>
                      {isContractFiles && (
                        <Row className="mb-2">
                          <Col>
                            <div className="upload-hint">
                              <span className="upload-hint__icon">
                                <HelpToolTip text="You can upload files for multiple contracts at once. Each file will be assigned to a contract in the next step." />
                              </span>
                              <span>
                                Drop all your documents here — you'll assign
                                each to a contract in the next step.
                              </span>
                            </div>
                          </Col>
                        </Row>
                      )}
                      <Row className="mb-3">
                        <Col>
                          <input
                            ref={addMoreRef}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={(e) => {
                              addFiles(e.target.files);
                              e.target.value = "";
                            }}
                            accept={fileAcceptValue}
                          />
                          <div className="upload-file-area">
                            <FileDropZone
                              onFiles={addFiles}
                              multiple
                              totalSize={totalSize}
                              totalLimit={TOTAL_SIZE_LIMIT}
                            />
                            {files.length > 0 && (
                              <FileList
                                files={files}
                                onRemove={removeFile}
                                showAddMore
                                onAddMore={() => addMoreRef.current?.click()}
                              />
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Button
                            variant="dark"
                            className="upload-submit-btn"
                            disabled={
                              files.length === 0 ||
                              files.some((f) => f.error) ||
                              isOverTotalLimit
                            }
                            onClick={() =>
                              isContractFiles
                                ? setShowModal(true)
                                : submitUpload(null)
                            }
                          >
                            <ArrowUpCircle size={15} />
                            {isContractFiles ? "Assign contracts →" : "Upload"}
                          </Button>
                        </Col>
                      </Row>
                    </>
                  )}
                  {user &&
                    user.customers &&
                    user.customers.length > 0 &&
                    user.acceptCreditCard &&
                    canAcceptCreditCard(user.customers) && (
                      <Row className="mt-5">
                        <Col>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              padding: "14px 18px",
                              background: "#f0f6fc",
                              borderRadius: "10px",
                              border: "1px solid #cce0f5",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#1a1a2e",
                                  marginBottom: "2px",
                                }}
                              >
                                Credit card payment
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "var(--upload-text-muted)",
                                }}
                              >
                                Click the button to make a secure credit card
                                payment.
                              </div>
                            </div>
                            <Link
                              to={"/payment"}
                              target="_blank"
                              rel="noopener"
                              id="checkout_button"
                              className="btn btn-sm"
                              style={{
                                fontSize: "13px",
                                backgroundColor: "#367bb0",
                                color: "white",
                                borderRadius: "4px",
                                padding: "7px 18px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Make a payment
                            </Link>
                          </div>
                        </Col>
                      </Row>
                    )}
                </Col>
              </Row>
              {showModal && (
                <AssignModal
                  files={files}
                  onClose={() => setShowModal(false)}
                  api={props.api}
                  defaultContract={defaultContract}
                  contractFilesUploadTypes={contractFilesUploadTypes}
                  handleDocumentUploadSubmit={handleDocumentUploadSubmitted}
                  customerCode={customerCode}
                />
              )}
            </Panel>

            <Panel
              header="Document Upload History"
              eventKey="2"
              style={{ marginTop: "10px" }}
            >
              <DocumentUploadHistory />
            </Panel>
          </>
        )}
      </Container>
    </>
  );
};

export default withHeaderMessages(WithApiAccess(UploadPage));
